import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { VisitorService } from './visitor.service';
import { shareReplay, take } from 'rxjs/operators';
import { PaymentService } from '../../shared/_services/payment.service';
import { normalizePlanType } from 'src/app/_configs/plan-types';

/**
 * The payments API serialises its response as a Map<String,String>, so booleans
 * arrive as the strings "true"/"false" — and !!"false" is true.
 */
function parseBooleanFlag(value: any): boolean {
  return typeof value === 'string' ? value.trim().toLowerCase() === 'true' : !!value;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly STORAGE_KEY = 'user';

  private cachedUser: any = null;

  userId: string | null = null;
  sessionId: string | null = null;

  private userSubject = new BehaviorSubject<any | null>(null);
  user$ = this.userSubject.asObservable().pipe(shareReplay(1));

  constructor(private visitorService: VisitorService, private paymentService: PaymentService) {
    this.loadUserFromStorage();
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined';
  }

  private loadUserFromStorage(): void {
    const user = this.getEncryptedUser();
    this.userSubject.next(user);
    if (user?.userData?.userId) {
      this.userId = user.userData.userId;
    }
  }

  private encrypt(data: any): string {
    return btoa(JSON.stringify(data));
  }

  private decrypt(data: string): any {
    try {
      return JSON.parse(atob(data));
    } catch (error) {
      console.error('Decryption failed', error);
      return null;
    }
  }

  // LocalStorage Helpers
  private getEncryptedUser(): any | null {
    if (this.cachedUser) return this.cachedUser;
    if (!this.isBrowser()) return null;
    const encrypted = localStorage.getItem(this.STORAGE_KEY);
    if (!encrypted) return null;

    const user = this.decrypt(encrypted);
    this.cachedUser = user;
    return user;
  }

  private saveUser(data: any): void {
    if (!this.isBrowser()) return;
    localStorage.setItem(this.STORAGE_KEY, this.encrypt(data));
    this.cachedUser = data;
    this.userSubject.next(data);
  }

  login(username: string, userData: any): boolean {
    if (this.isBrowser() && username) {
      this.saveUser({ username, userData });
      this.userId = userData.userId;
      this.paymentService.clearActivePlanDetailsCache(this.userId || ''); 
      return true;
    }
    return false;
  }

  logout(): void {
    if (!this.isBrowser()) return;
    this.paymentService.clearActivePlanDetailsCache(this.userId || '');
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem('sessionId');
    this.userId = null;
    this.sessionId = null;
    this.cachedUser = null;
    this.userSubject.next(null);
  }

  isLoggedIn(): boolean {
    return !!this.getEncryptedUser();
  }

  getUserDetails(): { id: string; status: 'loggedin' | 'nonloggedin' } {
    // No localStorage during SSR — treat the server render as a visitor.
    if (!this.isBrowser()) {
      return { id: this.userId || '', status: this.userId ? 'loggedin' : 'nonloggedin' };
    }

    const user = this.getEncryptedUser();
    if (user?.userData?.userId) {
      this.userId = user.userData.userId;
      localStorage.removeItem('sessionId');
    }

    if (!this.userId) {
      this.sessionId = localStorage.getItem('sessionId') || this.generateUUID();
      localStorage.setItem('sessionId', this.sessionId);
      return { id: this.sessionId, status: 'nonloggedin' };
    }

    return { id: this.userId, status: 'loggedin' };
  }

  /**
   * The signed-in user's id, or null for a visitor.
   *
   * Use this — not `getUserDetails().id` — wherever a feature needs a real
   * account. getUserDetails() always returns an id, falling back to a generated
   * session id for visitors, so `if (!userId)` never fires and login-only
   * features quietly let guests through. That is how guest session ids ended up
   * in the toolkit's user_upload_usage records.
   */
  getLoggedInUserId(): string | null {
    const { id, status } = this.getUserDetails();
    return status === 'loggedin' && id ? id : null;
  }

  getDecryptedUser(): any | null {
    return this.getEncryptedUser();
  }

  get accessToken(): string | null {
    return this.getEncryptedUser()?.userData?.accessToken || null;
  }

  getUserPackage(): 'trial' | 'basic' | 'standard' | 'advance' | 'none' | 'pending' {
    if (!this.isBrowser()) return 'none';

    const user = this.getEncryptedUser();
    if (!user) return 'pending';

    // advancemax is Advance on a 2-year term; normalising keeps it from falling
    // through to 'none' and stripping a paying customer of all access.
    const plan = normalizePlanType(user.userData.plan);
    const expiry = Number(user.userData.subscriptionExpiresAt ?? 0);
    const now = Date.now();

    // An elapsed expiry revokes the plan whatever it was. The previous shape
    // only expiry-checked 'advance' and then fell through to a check that
    // returned the plan regardless, so the guard never actually denied anyone.
    if (expiry && now >= expiry) {
      return 'none';
    }

    if (['trial', 'basic', 'standard', 'advance'].includes(plan)) {
      return plan as 'trial' | 'basic' | 'standard' | 'advance';
    }

    return 'none';
  }

  /* @@ AI-COMMENT: Retrieve plan and days left, comparing Payment API and login/signup data to use the furthest future expiry date */
  async getPlanAndDaysLeft(): Promise<{ plan: string, daysLeft: number | null, isTrial: boolean }> {
    if (this.isLoggedIn()) {
      const user = this.getEncryptedUser();
      const userData = user?.userData;
      const userId = userData?.userId;

      if (userId) {
        let paymentExpiry: number | null = null;
        let loginExpiry: number | null = userData?.subscriptionExpiresAt ? Number(userData.subscriptionExpiresAt) : null;
        let plan: string = userData?.plan || 'none';
        // The payments API's own verdict, when it answered. Null means it did not.
        let apiExpired: boolean | null = null;
        // Defaults to false (paid) until the backend's isTrial flag says otherwise,
        // so paid subscribers never see trial wording.
        let isTrial = false;

        try {
          const activePlanResponse = await this.paymentService.fetchActivePlanDetailsByUserId(userId).pipe(take(1)).toPromise();
          if (activePlanResponse && activePlanResponse.planExpiresAt) {
            paymentExpiry = Number(activePlanResponse.planExpiresAt);
            // Normalize payment expiry
            if (paymentExpiry > 0 && paymentExpiry < 10000000000) paymentExpiry *= 1000;
            plan = activePlanResponse.planType || plan;
            // The payments API serialises its whole response as strings, so a
            // plain truthiness check reads "false" as true. Parse it properly.
            isTrial = parseBooleanFlag(activePlanResponse.isTrial);
            apiExpired = parseBooleanFlag(activePlanResponse.isExpired);
          }
        } catch (error) {
          console.warn('Failed to fetch active plan details, falling back to cached user data:', error);
        }

        // Normalize login expiry
        if (loginExpiry && loginExpiry > 0 && loginExpiry < 10000000000) {
          loginExpiry *= 1000;
        }

        // The payments API is the live source of truth. The login payload is only
        // a snapshot taken at sign-in and goes stale the moment a plan changes,
        // so it is a fallback for when the API had nothing to say — never a
        // competitor to it. Taking the furthest of the two used to keep a
        // cancelled or shortened plan alive until the user logged in again.
        const expiry = paymentExpiry !== null ? paymentExpiry : (loginExpiry || 0);

        if (expiry > 0) {
          const now = Date.now();
          const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
          const source = paymentExpiry !== null ? 'PaymentAPI' : 'LoginResponse';

          // A plan whose expiry has passed grants nothing. Report it as 'none'
          // so every downstream access check revokes it — previously the plan
          // name was returned regardless and daysLeft simply went negative,
          // which only drove the banner while leaving access wide open.
          // The API's own flag wins when present; the date is the fallback.
          const expired = apiExpired !== null ? apiExpired : expiry <= now;
          const effectivePlan = expired ? 'none' : plan.toLowerCase();

          console.log(`[SubscriptionCheck] Source: ${source}, Expiry: ${expiry}, Now: ${now}, DaysLeft: ${daysLeft}, Plan: ${plan}, Effective: ${effectivePlan}, Expired: ${expired}, IsTrial: ${isTrial}`);
          return { plan: effectivePlan, daysLeft, isTrial };
        }
      }
    }
    return this.visitorService.getSubscriptionInfo();
  }

  /**
   * A v4 UUID, used as the session id for visitors who are not logged in.
   *
   * The template is spelled out rather than built as `1e7 + -1e3 + ...`. That
   * expression only produces the template when the first term is an array and
   * the pluses concatenate; as bare numbers they add up instead, collapsing it
   * to -99990013000. Every id then came out as "-9999xxx3xxx" — 9 and 3 are not
   * in the [018] class, so they survived unreplaced — and those ids reached the
   * toolkit as the user_id for guest uploads.
   */
  generateUUID(): string {
    if (!this.isBrowser()) return '';
    const template = '10000000-1000-4000-8000-100000000000';
    return template.replace(/[018]/g, (c: string) => {
      const random = crypto.getRandomValues(new Uint8Array(1))[0];
      return (Number(c) ^ (random & 15 >> (Number(c) / 4))).toString(16);
    });
  }

  /* @@ AI-COMMENT: Determine if the trial banner should be shown based on plan and days left, returning null for daysLeft if data is missing. Only shown to logged-in users. */
  async shouldShowTrialBanner(): Promise<{ show: boolean, daysLeft: number | null, isTrial: boolean }> {
    if (!this.isLoggedIn()) {
      return { show: false, daysLeft: null, isTrial: false };
    }

    const { plan, daysLeft, isTrial } = await this.getPlanAndDaysLeft();
    const normalizedPlan = normalizePlanType(plan) || 'none';

    // 'trial' is the free trial's own plan; 'advance' stays listed because
    // trials granted before TRIAL existed are still recorded as advance.
    const show =
      (normalizedPlan === 'trial' && daysLeft !== null && daysLeft <= 6) ||
      (normalizedPlan === 'advance' && daysLeft !== null && daysLeft <= 6) ||
      (normalizedPlan === 'none' && daysLeft !== null && daysLeft <= 6);

    return { show, daysLeft: show ? daysLeft : null, isTrial };
  }

}

